import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    Collapse,
    IconButton,
    Skeleton,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTranslation} from "react-i18next";
import DatasetTablePreview from "./dataset-table-preview";
import EffectChip from "./effect-chip";
import RequirementCallout from "./requirement-callout";

const SelectDatasetButton = ({onClick, label}) => (
    <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onClick}
        sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            width: {xs: '100%', md: 'auto'},
        }}
    >
        {label}
    </Button>
);

const DatasetCard = ({
                          entry,
                          option,
                          expanded,
                          onExpand,
                          onSelect,
                      }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport.dataSourceCatalog'});

    const handleSelectClick = (event) => {
        event.stopPropagation();
        onSelect(option);
    };

    const handleToggle = () => {
        onExpand(option.id, !expanded);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1.5,
                bgcolor: 'background.paper',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    gap: {xs: 1.5, md: 2},
                    px: 2,
                    py: 1.5,
                    alignItems: {xs: 'stretch', md: 'flex-start'},
                    cursor: 'pointer',
                }}
                onClick={handleToggle}
            >
                <Box sx={{display: 'flex', gap: 1, minWidth: 0, flex: 1}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, flex: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{minWidth: 0}}>
                                {entry.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                {t('columnCount', {count: entry.columnCount})}
                            </Typography>
                        </Box>
                        {entry.description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: expanded ? 'unset' : 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    lineHeight: 1.5,
                                }}
                            >
                                {entry.description}
                            </Typography>
                        )}
                        {entry.effect && (
                            <EffectChip effect={entry.effect} />
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: {xs: 'space-between', md: 'flex-end'},
                        gap: 1,
                        flexShrink: 0,
                        alignSelf: {xs: 'stretch', md: 'center'},
                    }}
                >
                    <SelectDatasetButton
                        label={t('selectDataset')}
                        onClick={handleSelectClick}
                    />
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleToggle();
                        }}
                        aria-expanded={expanded}
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: (theme) => theme.transitions.create('transform', {duration: 200}),
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={expanded} timeout="auto">
                <Box sx={{px: 2, pb: 2, pt: 0}}>
                    <Box sx={{borderTop: '1px solid', borderColor: 'divider', pt: 2}}>
                        {entry.detail && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{mb: 2, lineHeight: 1.6}}
                            >
                                {entry.detail}
                            </Typography>
                        )}

                        {entry.columnsLoading ? (
                            <Box sx={{mt: 1}}>
                                <Skeleton variant="rounded" height={140} sx={{borderRadius: 1}} />
                                <Skeleton variant="text" sx={{mt: 2}} />
                                <Skeleton variant="text" width="80%" />
                            </Box>
                        ) : (
                            <>
                                <DatasetTablePreview columns={entry.columns} />
                                <RequirementCallout rules={entry.requirementRules} />
                                {entry.alerts?.map((alert) => (
                                    <Alert
                                        key={alert.message}
                                        severity={alert.severity ?? 'info'}
                                        variant="outlined"
                                        sx={{mt: 1.5, borderRadius: 1}}
                                    >
                                        {alert.message}
                                    </Alert>
                                ))}
                            </>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Card>
    );
};

export default DatasetCard;
